/**
 * Iter-60 — Mobile slide-over navigation drawer.
 *
 * Renders on top of the shared Sidebar so mobile users get the *same*
 * role-scoped nav items — no separate mobile permission surface.
 *
 * Features:
 *   * Backdrop that dismisses on click.
 *   * Escape key closes the drawer.
 *   * Body scroll lock while open.
 *   * Auto-close after any nav link is clicked (handled by parent by
 *     passing a wrapped `onNavigate` callback).
 *   * a11y: `aria-modal`, `aria-hidden`, focus into the drawer on open.
 */
import React, { useEffect, useRef } from "react";
import { X } from "@phosphor-icons/react";

export default function MobileNavDrawer({ open, onClose, children }) {
  const closeBtnRef = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    // Body scroll lock.
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus close for a11y.
    setTimeout(() => closeBtnRef.current?.focus(), 50);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  return (
    <div
      aria-hidden={!open}
      inert={!open}
      className={`fixed inset-0 z-50 md:hidden ${open ? "" : "pointer-events-none"}`}
      data-testid="mobile-nav-drawer-wrapper"
    >
      {/* Backdrop */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/60 transition-opacity duration-200 ${
          open ? "opacity-100" : "opacity-0"
        }`}
        data-testid="mobile-nav-drawer-backdrop"
      />
      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
        className={`absolute inset-y-0 left-0 w-[85%] max-w-[320px] bg-white border-r border-black transform transition-transform duration-200 ${
          open ? "translate-x-0 shadow-[6px_0_0_0_#000]" : "-translate-x-full invisible"
        } flex flex-col`}
        data-testid="mobile-nav-drawer"
      >
        <div className="flex items-center justify-between border-b border-black px-4 py-3">
          <span className="font-chivo font-black uppercase tracking-tighter text-lg">
            Menu
          </span>
          <button
            ref={closeBtnRef}
            onClick={onClose}
            data-testid="mobile-nav-drawer-close"
            className="w-11 h-11 flex items-center justify-center border border-black hover:bg-black hover:text-white"
            aria-label="Close menu"
          >
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </aside>
    </div>
  );
}
